import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useRecoilState } from "recoil";
import useAuth from "../../hooks/useAuth";
import { useNotification } from "../../hooks/useNotification";
import { useZoomSettings } from "../../hooks/useZoomSettings";
import { notificationModalOpenState } from "../../recoil/notification";
import Dashboard from "./tabs/dashboard/Dashboard";
import DashboardMobile from "./tabs/dashboard/DashboardMobile";
import MyAdvertisement from "./tabs/myAdvertisement/MyAdvertisement";
import Settings from "./tabs/Settings";
import Deposit from "./tabs/deposit/Deposit";
import AddAdvertisement from "./tabs/addAdvertisement/AddAdvertisement";
import CheckerManagement from "./tabs/checkerManagement/CheckerManagement";
import RankCheck from "./tabs/rankKeyword/RankCheck";
import KeywordAnalysis from "./tabs/rankKeyword/KeywordAnalysis";
import NotificationModal from "../../components/NotificationModal";
import FAQModal from "../../components/FAQModal";
import NoticeIsland from "../../components/NoticeIsland/NoticeIsland";
import GuidePage from "../guide/GuidePage";
import GuideArticlePage from "../guide/GuideArticlePage";
import { MdBarChart, MdSearch } from "react-icons/md";
import menuIcon from "../../assets/icons/menu_m.svg";
import logoIcon from "../../assets/images/logo.png";
import bellIcon from "../../assets/images/bell.png";
import dashboardIcon from "../../assets/icons/deshboard.svg";
import dashboardIconActive from "../../assets/icons/deshboardActive.svg";
import advertismentIcon from "../../assets/icons/advertismentIcon.svg";
import advertismentIconActive from "../../assets/icons/advertismentIconActive.svg";
import checkAdvertisementIcon from "../../assets/icons/checkAdvertisement.svg";
import checkAdvertisementIconActive from "../../assets/icons/checkAdvertisementActive.svg";
import balanceIcon from "../../assets/icons/balance.svg";
import balanceIconActive from "../../assets/icons/balanceActive.svg";
import bookIcon from "../../assets/icons/book.svg";
import infoIcon from "../../assets/icons/info.svg";
import kakaotalkIcon from "../../assets/icons/kakaotalk.svg";
import "./HomeMobile.css";

/** price_grade.name → 뱃지 표기/톤 (TOP1→TOP, 미매칭→원문+TOP UI) */
function resolvePriceGradeBadge(gradeName) {
  const raw = String(gradeName || "").trim();
  if (!raw) return { label: "", tone: "top" };
  const upper = raw.toUpperCase();
  if (upper.includes("VVIP")) return { label: "VVIP", tone: "vvip" };
  if (upper.includes("VIP")) return { label: "VIP", tone: "vip" };
  if (upper.includes("BASIC")) return { label: "BASIC", tone: "basic" };
  if (upper.includes("TOP")) return { label: "TOP", tone: "top" };
  return { label: raw, tone: "top" };
}

const HomeMobile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 디버깅: user 객체 확인
  useEffect(() => {
    console.log("HomeMobile - user:", user);
    console.log("HomeMobile - user?.role:", user?.role);
    console.log("HomeMobile - is AGENCY?", user?.role === "AGENCY");
  }, [user]);
  const [activeTab, setActiveTab] = useState("myAdvertisement");
  const [depositSubTab, setDepositSubTab] = useState("chargeRequest");
  const [tempDepositSubTab, setTempDepositSubTab] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // 알림 관련
  const { unreadCount, fetchNotifications } = useNotification();
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useRecoilState(
    notificationModalOpenState,
  );
  const [isFAQModalOpen, setIsFAQModalOpen] = useState(false);

  // Zoom Settings 관련
  const { settings } = useZoomSettings();

  // URL 파라미터에서 원하는 탭 확인. 가이드는 /guide 고정 경로라 탭 쿼리로 바꾸지 않는다.
  useEffect(() => {
    if (location.pathname.startsWith("/guide")) return;

    const params = new URLSearchParams(location.search);
    const desiredTab = params.get("tab");
    const desiredSubTab = params.get("subTab");

    if (
      desiredTab &&
      [
        "dashboard",
        "users",
        "settings",
        "deposit",
        "addAdvertisement",
        "myAdvertisement",
        "checkerManagement",
        "rankCheck",
        "keywordAnalysis",
        "rankKeyword",
      ].includes(desiredTab)
    ) {
      // 대시보드 탭은 비노출 — URL 진입 시 광고 확인으로 리다이렉트
      if (desiredTab === "dashboard") {
        setActiveTab("myAdvertisement");
        navigate("/?tab=myAdvertisement", { replace: true });
        return;
      }

      // 구 통합 탭 → 순위 체크로 리다이렉트
      if (desiredTab === "rankKeyword") {
        setActiveTab("rankCheck");
        navigate("/?tab=rankCheck", { replace: true });
        return;
      }

      // CHECKER 역할은 광고 등록, 예치금 관리 접근 불가
      if (user?.role === "CHECKER") {
        if (
          desiredTab === "addAdvertisement" ||
          desiredTab === "deposit"
        ) {
          setActiveTab("myAdvertisement");
          navigate("/?tab=myAdvertisement", { replace: true });
          return;
        }
      }

      // 체커 관리는 AGENCY만 접근
      if (desiredTab === "checkerManagement" && user?.role !== "AGENCY") {
        setActiveTab("myAdvertisement");
        navigate("/?tab=myAdvertisement", { replace: true });
        return;
      }

      setActiveTab(desiredTab);

      // 서브탭이 있는 경우 처리
      if (desiredSubTab && desiredTab === "deposit") {
        setTempDepositSubTab(desiredSubTab);
      }
    } else {
      // URL 파라미터가 없으면 광고 확인이 기본
      setActiveTab("myAdvertisement");
      navigate("/?tab=myAdvertisement", { replace: true });
    }
  }, [location.pathname, location.search, navigate, user?.role]);

  const handleLogout = async () => {
    // logout() 함수가 이미 window.location.replace로 리다이렉트하므로
    // navigate는 호출하지 않음 (중복 리다이렉트 방지)
    setIsSidebarOpen(false);
    await logout();
    navigate("/login");
  };

  const handleFAQClick = () => {
    setIsSidebarOpen(false);
    setIsFAQModalOpen(true);
  };

  const handleKakaoChat = () => {
    if (settings?.kakaoChannelId) {
      window.open(settings.kakaoChannelId, "_blank");
    } else {
      alert("카카오톡 상담 링크를 준비 중입니다.");
    }
  };

  const handleNotificationClick = async () => {
    await fetchNotifications(1);
    setIsNotificationModalOpen(true);
  };

  const handleTabClick = (tabName, subTab = null, statusFilter = null) => {
    // 모바일에서 탭 클릭 시 사이드바 닫기
    setIsSidebarOpen(false);

    // 대시보드 탭은 비노출 — 클릭/호출 시 광고 확인으로 이동
    if (tabName === "dashboard") {
      setActiveTab("myAdvertisement");
      navigate("/?tab=myAdvertisement", { replace: true });
      return;
    }

    // CHECKER 역할은 광고 등록, 예치금 관리 접근 불가
    if (user?.role === "CHECKER") {
      if (tabName === "addAdvertisement" || tabName === "deposit") {
        return;
      }
    }

    // 예치금 관리 탭으로 이동하면서 서브탭 지정
    if (tabName === "deposit" && subTab) {
      setTempDepositSubTab(subTab);
    } else {
      setTempDepositSubTab(null);
    }

    setActiveTab(tabName);
    const statusQuery =
      tabName === "myAdvertisement" && statusFilter
        ? `&status=${statusFilter}`
        : "";
    navigate(
      `/?tab=${tabName}${subTab ? `&subTab=${subTab}` : ""}${statusQuery}`,
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <DashboardMobile onTabChange={handleTabClick} />;
      case "addAdvertisement":
        return <AddAdvertisement onTabChange={handleTabClick} />;
      case "myAdvertisement":
        return <MyAdvertisement />;
      case "checkerManagement":
        return <CheckerManagement />;
      case "rankCheck":
        return <RankCheck />;
      case "keywordAnalysis":
        return <KeywordAnalysis />;
      case "rankKeyword":
        return <RankCheck />;
      case "settings":
        return <Settings />;
      case "deposit":
        return <Deposit initialTab={tempDepositSubTab || depositSubTab} />;
      default:
        return <MyAdvertisement />;
    }
  };

  const isGuideRoute = location.pathname.startsWith("/guide");
  const isGuideArticle = /^\/guide\/.+/.test(location.pathname);
  const mobileTab = new URLSearchParams(location.search).get("tab");
  const isRankCheckSeo = mobileTab === "rankCheck" || mobileTab === "rankKeyword";
  const isKeywordAnalysisSeo = mobileTab === "keywordAnalysis";
  const gradeBadge = user?.price_grade?.name
    ? resolvePriceGradeBadge(user.price_grade.name)
    : null;

  return (
    <div className="home-mobile-container">
      {!isGuideRoute && (
      <Helmet>
        {isRankCheckSeo ? (
          <>
            <title>
              올려줌 순위 체크 | 스마트스토어 상품·키워드 광고 순위
            </title>
            <meta
              name="description"
              content="올려줌(olzoom.com 공식)에서 스마트스토어 간편 상품·키워드 광고 순위를 확인하고 관리하세요. 셀프 마케팅 플랫폼."
            />
            <meta
              name="keywords"
              content="올려줌,olzoom,스마트스토어순위,네이버광고순위,순위체크,쇼핑순위"
            />
            <meta name="robots" content="index,follow" />
            <link
              rel="canonical"
              href="https://www.olzoom.com/?tab=rankCheck"
            />
          </>
        ) : isKeywordAnalysisSeo ? (
          <>
            <title>올려줌 키워드 분석 | 네이버 연관 키워드·검색량</title>
            <meta
              name="description"
              content="올려줌(olzoom.com 공식)에서 네이버 연관 키워드 검색량·경쟁도·블로그 포화도를 분석하세요. 셀프 마케팅 플랫폼."
            />
            <meta
              name="keywords"
              content="올려줌,olzoom,연관키워드,키워드분석,검색량,네이버키워드"
            />
            <meta name="robots" content="index,follow" />
            <link
              rel="canonical"
              href="https://www.olzoom.com/?tab=keywordAnalysis"
            />
          </>
        ) : (
          <>
            <title>올려줌 - 순위 상위 노출 셀프 마케팅</title>
            <meta
              name="description"
              content="인스타그램, 유튜브, 틱톡, 스레드, 스마트스토어, 네이버플레이스까지 검색·방문·리뷰·저장·노출을 실제 유저가 직접 실행하는 리워드 트래픽 기반 마케팅 셀프마케팅의 해답, 셀러 대행사 중심 실행 플랫폼 올려줌."
            />
            <meta name="robots" content="index,follow" />
            <link rel="canonical" href="https://www.olzoom.com/" />
          </>
        )}
      </Helmet>
      )}

      {/* 헤더 */}
      <div className="home-mobile-header">
        <div className="home-mobile-header-bar">
          <div className="home-mobile-header-left">
            <button
              className="home-mobile-hamburger-btn"
              onClick={() => setIsSidebarOpen(true)}
            >
              <img src={menuIcon} alt="메뉴" className="home-mobile-menu-icon" />
            </button>
          </div>
          <div className="home-mobile-header-center">
            <img src={logoIcon} alt="올려줌" className="home-mobile-logo" />
          </div>
          <div className="home-mobile-header-right">
            <button
              className="home-mobile-notification-btn"
              onClick={handleNotificationClick}
              style={{ position: "relative" }}
            >
              <img
                src={bellIcon}
                alt="알림"
                className="home-mobile-notification-icon"
              />
              {unreadCount > 0 && (
                <span className="home-mobile-notification-badge">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>
        <div className="home-mobile-header-island">
          <NoticeIsland />
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="home-mobile-main-content">
        {isGuideRoute ? (
          isGuideArticle ? (
            <GuideArticlePage />
          ) : (
            <GuidePage />
          )
        ) : (
          renderTabContent()
        )}
      </div>

      {/* 사이드바 오버레이 */}
      <div
        className={`home-mobile-overlay ${isSidebarOpen ? "open" : ""}`}
        onClick={() => setIsSidebarOpen(false)}
      />

      {/* 모바일 사이드바 */}
      <div className={`home-mobile-sidebar ${isSidebarOpen ? "open" : ""}`}>
        {/* 사용자 정보 섹션 */}
        <div className="home-mobile-user-section">
          {user ? (
            <>
              <div className="home-mobile-user-info">
                {gradeBadge && (
                  <div className={`home-mobile-grade-tag is-${gradeBadge.tone}`}>
                    {gradeBadge.label}
                  </div>
                )}
                <span className="home-mobile-user-name">{user.name} 님</span>
              </div>

              {user?.deposit !== undefined && (
                <div className="home-mobile-deposit-info">
                  <span>예치금: {user.deposit.toLocaleString()} 원</span>
                </div>
              )}
            </>
          ) : (
            <div className="home-mobile-login-prompt">
              <span>로그인이 필요합니다</span>
              <button
                onClick={() => {
                  navigate("/login");
                  setIsSidebarOpen(false);
                }}
                className="home-mobile-login-btn"
              >
                로그인
              </button>
            </div>
          )}
        </div>

        {/* 네비게이션 메뉴 */}
        <nav className="home-mobile-nav">
          {/* 기존 dashboard 탭(위젯 홈) 일시 비노출 — URL 진입은 myAdvertisement로 리다이렉트 */}
          <button
            className={`home-mobile-nav-btn ${
              activeTab === "myAdvertisement" ? "active" : ""
            }`}
            onClick={() => handleTabClick("myAdvertisement")}
          >
            <img
              src={
                activeTab === "myAdvertisement"
                  ? dashboardIconActive
                  : dashboardIcon
              }
              alt="대시보드"
              className="home-mobile-nav-icon"
            />
            <span>대시보드</span>
          </button>
          <button
            className={`home-mobile-nav-btn ${
              activeTab === "addAdvertisement" ? "active" : ""
            } ${!user || user?.role === "CHECKER" ? "disabled" : ""}`}
            onClick={() => handleTabClick("addAdvertisement")}
            disabled={!user || user?.role === "CHECKER"}
          >
            <img
              src={
                activeTab === "addAdvertisement"
                  ? advertismentIconActive
                  : advertismentIcon
              }
              alt="광고 등록"
              className="home-mobile-nav-icon"
            />
            <span>광고 등록</span>
          </button>
          <button
            className={`home-mobile-nav-btn ${
              activeTab === "deposit" ? "active" : ""
            } ${!user || user?.role === "CHECKER" ? "disabled" : ""}`}
            onClick={() => handleTabClick("deposit")}
            disabled={!user || user?.role === "CHECKER"}
          >
            <img
              src={activeTab === "deposit" ? balanceIconActive : balanceIcon}
              alt="예치금 관리"
              className="home-mobile-nav-icon"
            />
            <span>예치금 관리</span>
          </button>
          {/* 체커 관리 - AGENCY 권한만 표시 */}
          {user?.role === "AGENCY" && (
            <>
              <button
                className={`home-mobile-nav-btn ${
                  activeTab === "checkerManagement" ? "active" : ""
                }`}
                onClick={() => handleTabClick("checkerManagement")}
              >
                <img
                  src={
                    activeTab === "checkerManagement"
                      ? checkAdvertisementIconActive
                      : checkAdvertisementIcon
                  }
                  alt="체커 관리"
                  className="home-mobile-nav-icon"
                />
                <span>체커 관리</span>
              </button>
            </>
          )}
          <button
            type="button"
            className={`home-mobile-nav-btn ${
              activeTab === "rankCheck" ? "active" : ""
            }`}
            onClick={() => handleTabClick("rankCheck")}
          >
            <MdBarChart className="home-mobile-nav-icon" />
            <span>순위 체크</span>
          </button>
          <button
            type="button"
            className={`home-mobile-nav-btn ${
              activeTab === "keywordAnalysis" ? "active" : ""
            }`}
            onClick={() => handleTabClick("keywordAnalysis")}
          >
            <MdSearch className="home-mobile-nav-icon" />
            <span>키워드 분석</span>
          </button>
        </nav>

        {/* 하단 메뉴 */}
        <div className="home-mobile-footer-menu">
          {/* 마케팅 게시글 — 사이드바 진입 비활성
          <Link
            to="/guide"
            className={`home-mobile-footer-btn${isGuideRoute ? " is-active" : ""}`}
            onClick={() => setIsSidebarOpen(false)}
          >
            <img
              src={bookIcon}
              alt="마케팅 게시글"
              className="home-mobile-footer-icon"
            />
            <span>마케팅 게시글</span>
          </Link>
          */}
          <button className="home-mobile-footer-btn" onClick={handleFAQClick}>
            <img
              src={infoIcon}
              alt="자주묻는질문"
              className="home-mobile-footer-icon"
            />
            <span>자주 묻는 질문</span>
          </button>
          <button className="home-mobile-footer-btn" onClick={handleKakaoChat}>
            <img
              src={kakaotalkIcon}
              alt="카카오톡 상담하기"
              className="home-mobile-footer-icon"
            />
            <span>카카오톡 상담하기</span>
          </button>
        </div>

        {/* 로그아웃 버튼 */}
        {user && (
          <div className="home-mobile-logout-section">
            <button onClick={handleLogout} className="home-mobile-logout-btn">
              로그아웃
            </button>
          </div>
        )}
      </div>

      {/* 알림 모달 */}
      <NotificationModal />

      {/* FAQ 모달 */}
      <FAQModal
        isOpen={isFAQModalOpen}
        onClose={() => setIsFAQModalOpen(false)}
      />
    </div>
  );
};

export default HomeMobile;
